import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    DATETIME_1_JAN_2022,
    LABEL_FOR_COUNTRIES_SEARCH_FIELD,
    MESSAGE_NOT_AVAILABLE_IN_DATE,
    MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_COUNTRY,
    ROUTE_PAGE_COUNTRY_PROFILE
} from '../../../constants';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { useCountryProfileFilters } from '../../../hooks/use-country-profile-filters.hook';
import { Taxonomy } from '../../../types';
import {
    getDefaultSelectedCountryId,
    getTaxonomyById,
    getTaxonomyName,
    interpolateString,
    joinStrings
} from '../../../utils';
import Box from '../../layout/box/box.component';
import Content from '../../layout/content/content.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import PageHeader from '../../layout/page-header/page-header.component';
import Page from '../../layout/page/page.component';
import ResultsButtonpad from '../../partials/results-buttonpad/results-buttonpad.component';
import Spacer from '../../layout/spacer/spacer.component';
import TaxonomyListGrouped from '../../partials/taxonomy-list-filter/taxonomy-list-filter-grouped.component';
import {
    countryProfileClassName,
    countryProfileFiltersCellClassName,
    countryProfileContentCellClassName,
    countryProfileFilterCountriesClassName,
    countryProfileDataWrapperClassName,
    countryProfileDataHeaderClassName,
    countryProfileCountryNameClassName,
    countryProfileRegionNameClassName,
    countryProfilePageSmallTextClassName,
    womenInLocalGovernmentLogoClassName,
    womenInLocalGovernmentTextClassName,
    womenInLocalGovernmentTextInnerClassName,
    countryProfileNoResultsTitleClassName,
    countryProfileNoResultsMessageClassName,
} from './country-profile-page.style';
import { CountryProfileDashboard } from '../../partials/country-profile-dashboard/country-profile-dashboard.component';
import { logoWomenInLocalGovernment } from '../../../assets';
import SearchField from '../../layout/form/search-field.component';
import { filterTaxonomies } from '../../partials/taxonomy-list-filter/taxonomy-list-filter.utils';


const NoResultsSection = ({
    localLevel = false,
    countryName,
}: {
    localLevel?: boolean,
    countryName?: string | null
}) => (
    <div>
        <Spacer x2 />
        <Spacer />
        <div className={ countryProfileNoResultsTitleClassName }>
            { localLevel ? 'Local government' : 'Parliament' }
        </div>
        <Spacer />
        <Box >
            <Spacer>
                <div className={ countryProfileNoResultsMessageClassName }>
                    {
                        countryName
                            ? interpolateString(MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_COUNTRY, {
                                country: countryName
                            })
                            : interpolateString(MESSAGE_NOT_AVAILABLE_IN_DATE, {
                                level: localLevel ? 'local' : 'national'
                            })
                    }
                </div>
            </Spacer>
        </Box>
    </div>
);

const UNWomenLink: React.FC<{children?: React.ReactNode}> = (
    {children}
) => (
    <a
        href='https://localgov.unwomen.org/'
        target='_blank'
        rel='noopener noreferrer'>
        {children}
    </a>
);

const CountryProfilePage: React.FC = () => {
    const [{
        taxonomies,
        countryProfiles
    }, {
        getCountryProfile,
        getPage
    }] = useAppState();
    const [searchString, setSearchString] = useState('');
    const [filters, changeFilters] = useCountryProfileFilters();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const page = getPage('Country Profiles');
    const countryIdFromParams = Number(params.get('countryId'));
    const countryId = countryIdFromParams || getDefaultSelectedCountryId(countryProfiles);
    const countryTaxonomy = countryId ? getTaxonomyById(taxonomies.countries, countryId) : null;
    const regionName = countryTaxonomy ? getTaxonomyName(taxonomies, 'regions', countryTaxonomy.regionId) : null;
    const countryName = countryTaxonomy
        ? joinStrings(' ', countryTaxonomy.prefix, countryTaxonomy.name)
        : null;

    const selectedCountryProfile = countryId
        ? getCountryProfile(countryId, false, filters.datetime)
        : null;

    const selectedCountryProfileLocalLevel = countryId
        ? getCountryProfile(countryId, true, filters.datetime)
        : null;

    const selectedCountryProfileTaxonomyNames = {
        country: '',
        region: '',
        parliamentStructure: '',
        electoralSystemType: '',
        quotaTargetMet: '',
        quotaForInputOnly: '',
        typeOfQuotas: '',
    };

    const selectedCountryProfileTaxonomyNamesLocalLevel = {
        country: '',
        region: '',
        parliamentStructure: '',
        electoralSystemType: '',
        quotaTargetMet: '',
        quotaForInputOnly: '',
        typeOfQuotas: '',
    };

    if (selectedCountryProfile) {
        selectedCountryProfileTaxonomyNames.country = getTaxonomyName(taxonomies, 'countries', selectedCountryProfile.countryId);
        selectedCountryProfileTaxonomyNames.region = getTaxonomyName(taxonomies, 'regions', selectedCountryProfile.regionId);
        selectedCountryProfileTaxonomyNames.parliamentStructure = getTaxonomyName(taxonomies, 'parliamentStructure', selectedCountryProfile.parliamentStructureId);
        selectedCountryProfileTaxonomyNames.electoralSystemType = getTaxonomyName(taxonomies, 'electoralSystemTypes', selectedCountryProfile.electoralSystemTypeId);
        selectedCountryProfileTaxonomyNames.quotaTargetMet = getTaxonomyName(taxonomies, 'quotaTargetMet', selectedCountryProfile.quotaTargetMetId);
        selectedCountryProfileTaxonomyNames.quotaForInputOnly = getTaxonomyName(taxonomies, 'quotaForInputOnly', selectedCountryProfile.quotaForInputOnlyId);
        selectedCountryProfileTaxonomyNames.typeOfQuotas = selectedCountryProfile.typeOfQuotaIds.map(typeOfQuotaId => getTaxonomyName(taxonomies, 'typesOfQuota', typeOfQuotaId)).join(', ');
    }

    if (selectedCountryProfileLocalLevel) {
        selectedCountryProfileTaxonomyNamesLocalLevel.country = getTaxonomyName(taxonomies, 'countries', selectedCountryProfileLocalLevel.countryId);
        selectedCountryProfileTaxonomyNamesLocalLevel.region = getTaxonomyName(taxonomies, 'regions', selectedCountryProfileLocalLevel.regionId);
        selectedCountryProfileTaxonomyNamesLocalLevel.parliamentStructure = getTaxonomyName(taxonomies, 'parliamentStructure', selectedCountryProfileLocalLevel.parliamentStructureId);
        selectedCountryProfileTaxonomyNamesLocalLevel.electoralSystemType = getTaxonomyName(taxonomies, 'electoralSystemTypes', selectedCountryProfileLocalLevel.electoralSystemTypeId);
        selectedCountryProfileTaxonomyNamesLocalLevel.quotaTargetMet = getTaxonomyName(taxonomies, 'quotaTargetMet', selectedCountryProfileLocalLevel.quotaTargetMetId);
        selectedCountryProfileTaxonomyNamesLocalLevel.quotaForInputOnly = getTaxonomyName(taxonomies, 'quotaForInputOnly', selectedCountryProfileLocalLevel.quotaForInputOnlyId);
        selectedCountryProfileTaxonomyNamesLocalLevel.typeOfQuotas = selectedCountryProfileLocalLevel.typeOfQuotaIds.map(typeOfQuotaId => getTaxonomyName(taxonomies, 'typesOfQuota', typeOfQuotaId)).join(', ');
    }

    const onClickCountryItem = (country: Taxonomy) => {
        changeFilters({
            datetime: null
        });
        navigate(ROUTE_PAGE_COUNTRY_PROFILE + '?countryId=' + country.id);
    };

    return (
        <Page>
            { page && (
                <PageHeader
                    title={ page.name }
                    description={ page.html }/>
            ) }
            <Content>
                <div className={ countryProfileClassName }>
                    <Grid>
                        <Cell size={ 12 } sizeTablet={ 'stack' } className={ countryProfileFiltersCellClassName }>
                            <Box transparent>
                                <Spacer>
                                    <SearchField
                                        value={ searchString }
                                        placeholder={ LABEL_FOR_COUNTRIES_SEARCH_FIELD }
                                        onChange={ setSearchString }/>
                                    <Spacer />
                                    <div className={ countryProfileFilterCountriesClassName }>
                                        <TaxonomyListGrouped
                                            list={ filterTaxonomies(taxonomies.countries, searchString) }
                                            searchField='Search for a country...'
                                            onClickItem={ onClickCountryItem }/>
                                    </div>
                                </Spacer>
                                <div className={ countryProfilePageSmallTextClassName }>
                                    Only countries with legislated gender quotas included
                                </div>
                            </Box>
                        </Cell>
                        <Cell size={ 12 } sizeTablet={ 'fill' } className={ countryProfileContentCellClassName }>
                            <div className={ countryProfileDataWrapperClassName } id='download'>
                                <div className={ countryProfileDataHeaderClassName }>
                                    <Grid>
                                        <Cell size={ 12 } sizeTablet={ 6 }>
                                            <div className={ countryProfileCountryNameClassName }>
                                                { countryName }
                                            </div>
                                            <div className={ countryProfileRegionNameClassName }>
                                                { regionName }
                                            </div>
                                        </Cell>
                                        <Cell size={ 12 } sizeTablet={ 6 }>
                                            <Spacer/>
                                                <ResultsButtonpad
                                                    pdf='Download country profile'
                                                    downloadData={ {
                                                        fileName: selectedCountryProfileTaxonomyNames.country + '\'s profile',
                                                        tables: () => {
                                                            const result = [];

                                                            if (selectedCountryProfile) {
                                                                result.push({
                                                                    title: selectedCountryProfileTaxonomyNames.country + '\'s profile',
                                                                    subtitle: 'Parliament',
                                                                    headers: ['Country', selectedCountryProfileTaxonomyNames.country],
                                                                    rows: [
                                                                        ['Region', selectedCountryProfileTaxonomyNames.region],
                                                                        ['Parliament structure', selectedCountryProfileTaxonomyNames.parliamentStructure],
                                                                        ['Electoral system', selectedCountryProfileTaxonomyNames.electoralSystemType],
                                                                        ['Electoral system (sub-type)', selectedCountryProfile.electoralSystemSubType],
                                                                        ['Type of legislated quota', selectedCountryProfileTaxonomyNames.typeOfQuotas],
                                                                        ['Quota target', selectedCountryProfile.quotaTarget + '%'],
                                                                        ['Women\'s representation', selectedCountryProfile.womensRepresentation + '%'],
                                                                        ['Quota met', selectedCountryProfileTaxonomyNames.quotaTargetMet],
                                                                        ['Provision: Constitution', `${selectedCountryProfile.constitutionLegalSource}. ${selectedCountryProfile.constitutionLegalProvisions}`],
                                                                        ['Provision: Electoral law', `${selectedCountryProfile.electoralLegalSource}. ${selectedCountryProfile.electoralLegalProvisions}`],
                                                                        ['Provision: Party law', `${selectedCountryProfile.partyLegalSource}. ${selectedCountryProfile.partyLegalProvisions}`],
                                                                        ['Provision: Parity law', `${selectedCountryProfile.parityLegalSource}. ${selectedCountryProfile.parityLegalProvisions}`],
                                                                        ['Provision: Financing law', `${selectedCountryProfile.financingLegalSource}. ${selectedCountryProfile.financingLegalProvisions}`],
                                                                        ['Provision: Political agreement', `${selectedCountryProfile.politicalLegalSource}. ${selectedCountryProfile.politicalLegalProvisions}`],
                                                                    ]
                                                                });
                                                            }

                                                            if (selectedCountryProfileLocalLevel) {
                                                                result.push({
                                                                    title: selectedCountryProfileTaxonomyNamesLocalLevel.country + '\'s profile',
                                                                    subtitle: 'Local government',
                                                                    headers: ['Country', selectedCountryProfileTaxonomyNamesLocalLevel.country],
                                                                    rows: [
                                                                        ['Region', selectedCountryProfileTaxonomyNamesLocalLevel.region],
                                                                        ['Parliament structure', selectedCountryProfileTaxonomyNamesLocalLevel.parliamentStructure],
                                                                        ['Electoral system', selectedCountryProfileTaxonomyNamesLocalLevel.electoralSystemType],
                                                                        ['Electoral system (sub-type)', selectedCountryProfileLocalLevel.electoralSystemSubType],
                                                                        ['Type of legislated quota', selectedCountryProfileTaxonomyNamesLocalLevel.typeOfQuotas],
                                                                        ['Quota target', selectedCountryProfileLocalLevel.quotaTarget + '%'],
                                                                        ['Women\'s representation', selectedCountryProfileLocalLevel.womensRepresentation + '%'],
                                                                        ['Provision: Constitution', `${selectedCountryProfileLocalLevel.constitutionLegalSource}. ${selectedCountryProfileLocalLevel.constitutionLegalProvisions}`],
                                                                        ['Provision: Electoral law', `${selectedCountryProfileLocalLevel.electoralLegalSource}. ${selectedCountryProfileLocalLevel.electoralLegalProvisions}`],
                                                                        ['Provision: Local government law', `${selectedCountryProfileLocalLevel.localLegalSource}. ${selectedCountryProfileLocalLevel.localLegalProvisions}`],
                                                                        ['Provision: Party law', `${selectedCountryProfileLocalLevel.partyLegalSource}. ${selectedCountryProfileLocalLevel.partyLegalProvisions}`],
                                                                        ['Provision: Parity law', `${selectedCountryProfileLocalLevel.parityLegalSource}. ${selectedCountryProfileLocalLevel.parityLegalProvisions}`],
                                                                        ['Provision: Financing law', `${selectedCountryProfileLocalLevel.financingLegalSource}. ${selectedCountryProfileLocalLevel.financingLegalProvisions}`],
                                                                        ['Provision: Political agreement', `${selectedCountryProfileLocalLevel.politicalLegalSource}. ${selectedCountryProfileLocalLevel.politicalLegalProvisions}`],
                                                                    ]
                                                                });
                                                            }

                                                            return result;
                                                        }
                                                    } }
                                                    datetime={ filters.datetime }
                                                    onChangeDate={ datetime => changeFilters({
                                                        datetime
                                                    }) }/>
                                            <Spacer/>
                                        </Cell>
                                    </Grid>
                                </div>
                                { selectedCountryProfile
                                    ? (
                                        <CountryProfileDashboard
                                            title='Parliament'
                                            countryProfile={ selectedCountryProfile }/>
                                    )
                                    : <NoResultsSection />
                                }
                                { selectedCountryProfileLocalLevel
                                    ? <>
                                        <CountryProfileDashboard
                                            title='Local government'
                                            countryProfile={ selectedCountryProfileLocalLevel }
                                            theme='green'/>
                                        <Spacer/>
                                        <Box transparent>
                                            <Spacer x2>
                                                <Grid>
                                                    <Cell size={ 'none' } sizeDesktop={ 3 }/>
                                                    <Cell size={ 12 } sizeTablet={ 8 } sizeDesktop={ 6 }>
                                                        <div className={ womenInLocalGovernmentTextClassName }>
                                                            <div className={ womenInLocalGovernmentTextInnerClassName }>
                                                                For more information, visit{' '}
                                                                <UNWomenLink>
                                                                    Women in Local Government website
                                                                </UNWomenLink>.
                                                            </div>
                                                        </div>
                                                    </Cell>
                                                    <Cell size={ 12 } sizeTablet={ 4 } sizeDesktop={ 3 }>
                                                        <div className={ womenInLocalGovernmentLogoClassName }>
                                                            <UNWomenLink>
                                                                <img src={ logoWomenInLocalGovernment } alt='Logo: Women in Local Government'/>
                                                            </UNWomenLink>
                                                        </div>
                                                    </Cell>
                                                </Grid>
                                            </Spacer>
                                        </Box>
                                    </>
                                    : (
                                        <NoResultsSection
                                            localLevel
                                            countryName={
                                                filters.datetime === DATETIME_1_JAN_2022
                                                    ? countryName
                                                    : undefined }
                                        /> 
                                    )
                                }
                            </div>
                        </Cell>
                    </Grid>
                </div>
            </Content>
        </Page>
    );
};

export default CountryProfilePage;
